/**
 * REQ-20260508-0001 #10182: 门户切换组件
 *
 * 用法：
 *   作为头像下拉菜单顶部的固定区块，仅当用户 available_portals.length >= 2 时渲染。
 *   平台超管 (user_scope=platform, portals=["platform"]) 不显示此组件。
 *
 * UI 对照原型 03-portal-switcher.html：
 *   - 顶部小标 "切换门户"
 *   - 当前门户卡片：emerald 边框 + ● 当前 标签
 *   - 其他门户卡片：可点击，hover indigo 边框
 *
 * 跳转策略：
 *   - admin → mall: window.location.href = '/mall' (跨前端项目)
 *   - mall → admin: window.location.href = '/tenant/admin/dashboard'
 *   - 同前端项目内（admin → /platform 等不存在此场景）: 仍可用 navigate
 *   - 同 token 复用：localStorage 共享，无需重新登录（同域不同路径）
 */

import React from 'react';
import { Tag } from 'antd';
import {
  ShoppingCartOutlined,
  SettingOutlined,
  RightOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useAuth } from '@/modules/auth';
import { openMallSecure } from '@/utils/authSync';

export interface PortalSwitcherProps {
  /** 当前所在门户（"admin" / "mall"）。基于路由或显式传入。 */
  currentPortal?: 'admin' | 'mall';
  /** 切换前回调（可用于关闭 Dropdown） */
  onBeforeSwitch?: () => void;
}

const PORTAL_META = {
  admin: {
    name: '管理后台',
    desc: '商品/库存/订单管理',
    icon: <SettingOutlined />,
    iconBg: 'linear-gradient(135deg, #6366f1, #3b82f6)',
    redirectPath: '/tenant/admin/dashboard',
    isExternal: false,
  },
  mall: {
    name: '采购商城',
    desc: '向上游下采购单',
    icon: <ShoppingCartOutlined />,
    iconBg: 'linear-gradient(135deg, #10b981, #14b8a6)',
    redirectPath: '/mall',
    isExternal: true, // web-mall 是另一前端项目
  },
} as const;

/**
 * 自动识别当前门户：基于 location.pathname
 */
function detectCurrentPortal(pathname: string): 'admin' | 'mall' | null {
  if (pathname === '/admin' || pathname.startsWith('/admin/') || pathname === '/tenant' || pathname.startsWith('/tenant/')) {
    return 'admin';
  }
  if (pathname === '/mall' || pathname.startsWith('/mall/')) {
    return 'mall';
  }
  return null;
}

const PortalSwitcher: React.FC<PortalSwitcherProps> = ({ currentPortal, onBeforeSwitch }) => {
  const { availablePortals, user } = useAuth();

  // 仅 tenant 用户 + 双 portal 才渲染（平台超管不渲染）
  const isPlatform = user?.user_scope === 'platform';
  const portals = (availablePortals || []) as ('admin' | 'mall' | 'platform')[];
  const hasDualPortal =
    !isPlatform &&
    portals.includes('admin') &&
    portals.includes('mall');

  if (!hasDualPortal) {
    return null;
  }

  // 自动识别当前门户（如果父组件未显式传入）
  const detectedCurrent = currentPortal || detectCurrentPortal(window.location.pathname) || 'admin';

  const handleSwitch = (target: 'admin' | 'mall') => {
    if (target === detectedCurrent) return; // 已在该门户
    if (onBeforeSwitch) onBeforeSwitch();

    if (target === 'mall') {
      // 跨域 SSO：必须用 openMallSecure() 新开窗口并通过 postMessage 传递 token
      openMallSecure();
    } else {
      window.location.assign(PORTAL_META[target].redirectPath);
    }
  };

  return (
    <div
      style={{
        padding: '12px 16px 8px',
        borderBottom: '1px solid #f1f5f9',
        background: '#fff',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: '#94a3b8',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        切换门户
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(['admin', 'mall'] as const).map((portal) => {
          const meta = PORTAL_META[portal];
          const isCurrent = portal === detectedCurrent;
          return (
            <button
              type="button"
              key={portal}
              aria-label={`切换到${meta.name}`}
              disabled={isCurrent}
              onClick={(e) => {
                e.stopPropagation();
                handleSwitch(portal);
              }}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                textAlign: 'left',
                font: 'inherit',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                border: `1.5px solid ${isCurrent ? '#10b981' : '#e2e8f0'}`,
                background: isCurrent ? '#ecfdf5' : '#fff',
                cursor: isCurrent ? 'default' : 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!isCurrent) {
                  e.currentTarget.style.borderColor = '#6366f1';
                  e.currentTarget.style.background = '#f5f3ff';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCurrent) {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = '#fff';
                }
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: meta.iconBg,
                  color: '#fff',
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {meta.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {meta.name}
                  {isCurrent && (
                    <Tag
                      color="success"
                      icon={<CheckCircleFilled />}
                      style={{ margin: 0, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}
                    >
                      当前
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{meta.desc}</div>
              </div>
              {!isCurrent && <RightOutlined style={{ fontSize: 10, color: '#cbd5e1' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PortalSwitcher;
